import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const AcademicInfoTab = ({ academicInfo, onUpdate }) => {
  const [formData, setFormData] = useState(academicInfo || {});
  const [isEditing, setIsEditing] = useState(false);
  
  // État local pour l'affichage de la note calculée en temps réel
  const [calculatedBac, setCalculatedBac] = useState(0);

  useEffect(() => {
    if (academicInfo) {
      setFormData(prev => ({
        ...prev,
        ...academicInfo,
        // On s'assure que les champs existent
        regionalGrade: academicInfo.regional_grade || '',
        continuousControlGrade: academicInfo.continuous_control_grade || '',
        nationalGrade: academicInfo.national_grade || '',
        biof: academicInfo.biof || false,
      }));
    }
  }, [academicInfo]);

  // --- CALCULATEUR AUTOMATIQUE BAC MAROC ---
  // Se déclenche à chaque fois que formData change
  useEffect(() => {
    const reg = parseFloat(formData.regionalGrade) || 0;
    const cont = parseFloat(formData.continuousControlGrade) || 0;
    const nat = parseFloat(formData.nationalGrade) || 0;

    if (reg > 0 && cont > 0 && nat > 0) {
      // Formule : (Régional * 0.25) + (Continu * 0.25) + (National * 0.50)
      const finalNote = (reg * 0.25) + (cont * 0.25) + (nat * 0.50);
      setCalculatedBac(finalNote.toFixed(2));
      
      // On met à jour la note globale dans les données à sauvegarder
      setFormData(prev => ({ ...prev, overallGrade: finalNote.toFixed(2) }));
    } else {
        setCalculatedBac(0);
    }
  }, [formData.regionalGrade, formData.continuousControlGrade, formData.nationalGrade]);


  // --- LISTE EXHAUSTIVE BAC MAROCAIN ---
  const baccalaureatOptions = [
    { value: 'Sciences Mathématiques A', label: 'Sciences Mathématiques A' },
    { value: 'Sciences Mathématiques B', label: 'Sciences Mathématiques B' },
    { value: 'Sciences Physiques', label: 'Sciences Physiques (PC)' },
    { value: 'SVT', label: 'Sciences de la Vie et de la Terre (SVT)' },
    { value: 'Sciences Agronomiques', label: 'Sciences Agronomiques' },
    { value: 'Sciences et Technologies Électriques (STE)', label: 'Sciences et Technologies Électriques (STE)' },
    { value: 'Sciences et Technologies Mécaniques (STM)', label: 'Sciences et Technologies Mécaniques (STM)' },
    { value: 'Sciences Économiques', label: 'Sciences Économiques' },
    { value: 'Sciences de Gestion Comptable', label: 'Sciences de Gestion Comptable (SGC)' },
    { value: 'Lettres', label: 'Lettres' },
    { value: 'Sciences Humaines', label: 'Sciences Humaines' },
    { value: 'Arts Appliqués', label: 'Arts Appliqués' },
    { value: 'Langue Arabe', label: 'Langue Arabe (Ens. Originel)' },
    { value: 'Sciences de la Chariaâ', label: 'Sciences de la Chariaâ' },
    { value: 'Bac Professionnel', label: 'Bac Professionnel' },
    { value: 'Autre', label: 'Autre' }
  ];

  const gradeLevelOptions = [
    { value: 'Tronc Commun', label: 'Tronc Commun' },
    { value: '1ère Année Bac', label: '1ère Année Bac' },
    { value: '2ème Année Bac', label: '2ème Année Bac' },
    { value: 'Post-Bac / Étudiant', label: 'Post-Bac / Étudiant' }
  ];

  const subjectOptions = [
    { value: 'Mathématiques', label: 'Mathématiques' },
    { value: 'Physique-Chimie', label: 'Physique-Chimie' },
    { value: 'SVT', label: 'SVT' },
    { value: 'Sciences de l\'Ingénieur', label: 'Sciences de l\'Ingénieur' },
    { value: 'Comptabilité', label: 'Comptabilité / Gestion' },
    { value: 'Économie', label: 'Économie Générale' },
    { value: 'Français', label: 'Français' },
    { value: 'Anglais', label: 'Anglais' },
    { value: 'Philosophie', label: 'Philosophie' }
  ];

  const careerInterestOptions = [
    { value: 'Ingénierie', label: 'Ingénierie' },
    { value: 'Médecine', label: 'Médecine / Santé' },
    { value: 'Commerce', label: 'Commerce / Gestion' },
    { value: 'Informatique', label: 'Informatique / IA' },
    { value: 'Architecture', label: 'Architecture' },
    { value: 'Droit', label: 'Droit' }
  ];

  const regionOptions = [
    { value: 'Casablanca', label: 'Casablanca' },
    { value: 'Rabat', label: 'Rabat' },
    { value: 'Marrakech', label: 'Marrakech' },
    { value: 'Tanger', label: 'Tanger' },
    { value: 'Fès', label: 'Fès' },
    { value: 'Autre', label: 'Autre' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // On transforme les champs snake_case (BDD) vers camelCase (Frontend) si besoin avant l'envoi
    // Mais ici on renvoie l'objet formData enrichi
    const dataToSave = {
        ...formData,
        regional_grade: formData.regionalGrade,
        continuous_control_grade: formData.continuousControlGrade,
        national_grade: formData.nationalGrade,
        biof: formData.biof
    };
    onUpdate(dataToSave);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(academicInfo || {});
    setIsEditing(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* --- SECTION 1 : PARCOURS SCOLAIRE --- */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="GraduationCap" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Mon Profil Bac</h3>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" iconName="Edit" iconPosition="left" onClick={() => setIsEditing(true)}>
              Modifier
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <Select
            label="Niveau Actuel"
            options={gradeLevelOptions}
            value={formData?.gradeLevel}
            onChange={(value) => handleInputChange('gradeLevel', value)}
            disabled={!isEditing}
            required
          />

          <div className="space-y-3">
             <Select
                label="Filière / Branche"
                options={baccalaureatOptions}
                value={formData?.baccalaureatTrack}
                onChange={(value) => handleInputChange('baccalaureatTrack', value)}
                disabled={!isEditing}
                required
                searchable
              />
              {/* CHECKBOX BIOF */}
              <div className="pt-1">
                 <Checkbox
                  label="Option BIOF (Bac International Français)"
                  checked={formData?.biof}
                  onChange={(e) => handleInputChange('biof', e?.target?.checked)}
                  disabled={!isEditing}
                />
              </div>
          </div>

          <Input
            label="Établissement Actuel"
            type="text"
            value={formData?.currentSchool}
            onChange={(e) => handleInputChange('currentSchool', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Ex: Lycée Lyautey"
          />

          <Input
            label="Année du Bac"
            type="number"
            value={formData?.baccalaureatYear}
            onChange={(e) => handleInputChange('baccalaureatYear', e?.target?.value)}
            disabled={!isEditing}
            placeholder="2025"
          />
        </div>
      </div>

      {/* --- SECTION 2 : CALCULATEUR DE NOTES --- */}
      <div className="bg-card border border-border rounded-lg p-6 relative overflow-hidden">
        {/* Un petit fond coloré pour distinguer cette section importante */}
        <div className="absolute top-0 right-0 p-4 opacity-5">
            <Icon name="Calculator" size={100} />
        </div>

        <div className="flex items-center space-x-3 mb-6 relative z-10">
          <Icon name="Calculator" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Mes Notes & Moyenne</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 relative z-10">
            {/* Note Régional (25%) */}
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
                <Input
                    label="Régional (25%)"
                    type="number"
                    value={formData?.regionalGrade}
                    onChange={(e) => handleInputChange('regionalGrade', e?.target?.value)}
                    disabled={!isEditing}
                    placeholder="Note / 20"
                    min="0"
                    max="20"
                    step="0.01"
                />
            </div>

            {/* Note Contrôle Continu (25%) */}
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
                <Input
                    label="Contrôle Continu (25%)"
                    type="number"
                    value={formData?.continuousControlGrade}
                    onChange={(e) => handleInputChange('continuousControlGrade', e?.target?.value)}
                    disabled={!isEditing}
                    placeholder="Moyenne 2ème année"
                    min="0"
                    max="20"
                    step="0.01"
                />
            </div>

            {/* Note National (50%) */}
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
                <Input
                    label="National (50%)"
                    type="number"
                    value={formData?.nationalGrade}
                    onChange={(e) => handleInputChange('nationalGrade', e?.target?.value)}
                    disabled={!isEditing}
                    placeholder="Note / 20"
                    min="0"
                    max="20"
                    step="0.01"
                />
            </div>
        </div>

        {/* --- RÉSULTAT DU CALCUL --- */}
        <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-primary mb-1">Note du Bac (Calculée)</p>
                <p className="text-xs text-muted-foreground">Mise à jour automatique</p>
            </div>
            <div className="text-3xl font-bold text-primary font-heading">
                {calculatedBac > 0 ? calculatedBac : '--'} <span className="text-sm font-normal text-muted-foreground">/ 20</span>
            </div>
        </div>
      </div>

      {/* --- SECTION 3 : PRÉFÉRENCES (Reste identique mais condensé) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="BookOpen" size={20} className="text-secondary" />
              <h3 className="font-semibold">Matières Fortes</h3>
            </div>
            <Select
              options={subjectOptions}
              value={formData?.preferredSubjects}
              onChange={(value) => handleInputChange('preferredSubjects', value)}
              disabled={!isEditing}
              multiple searchable
            />
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Target" size={20} className="text-accent" />
              <h3 className="font-semibold">Intérêts Pro.</h3>
            </div>
            <Select
              options={careerInterestOptions}
              value={formData?.careerInterests}
              onChange={(value) => handleInputChange('careerInterests', value)}
              disabled={!isEditing}
              multiple searchable
            />
          </div>
      </div>
      
       <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="MapPin" size={20} className="text-warning" />
          <h3 className="text-lg font-semibold text-foreground">Préférences Géographiques</h3>
        </div>
        <div className="space-y-4">
          <Select
            label="Villes d'études préférées"
            options={regionOptions}
            value={formData?.preferredRegions}
            onChange={(value) => handleInputChange('preferredRegions', value)}
            disabled={!isEditing}
            multiple
            searchable
          />
           <div className="space-y-3">
            <Checkbox
              label="Intéressé par les études à l'étranger"
              checked={formData?.acceptInternational}
              onChange={(e) => handleInputChange('acceptInternational', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end space-x-3 animate-fade-in pt-4">
          <Button variant="outline" onClick={handleCancel}>Annuler</Button>
          <Button variant="default" onClick={handleSave} iconName="Save" iconPosition="left">Enregistrer</Button>
        </div>
      )}
    </div>
  );
};

export default AcademicInfoTab;